import { useState, useEffect, useCallback, useRef } from 'react'
import type { HistoryEntry, HistoryFilter } from '../types/history'
import { listHistory } from '../services/history'
import { useInvalidationSubscription } from './use-list-invalidation'

const DEFAULT_PAGE = 1
const DEFAULT_PAGE_SIZE = 25
const DEBOUNCE_MS = 300

interface UseHistoryResult {
  entries: HistoryEntry[]
  total: number
  loading: boolean
  error: string | null
  filter: HistoryFilter
  setFilter: (filter: Partial<HistoryFilter>) => void
  page: number
  setPage: (page: number) => void
  pageSize: number
  refetch: () => Promise<void>
}

export function useHistory(caseId: string | undefined): UseHistoryResult {
  const [entries, setEntries] = useState<HistoryEntry[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilterState] = useState<HistoryFilter>({})
  const [page, setPage] = useState(DEFAULT_PAGE)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const fetchData = useCallback(
    async (currentFilter: HistoryFilter, currentPage: number) => {
      if (!caseId) {
        setEntries([])
        setTotal(0)
        setError(null)
        return
      }

      setLoading(true)
      setError(null)

      const offset = (currentPage - DEFAULT_PAGE) * DEFAULT_PAGE_SIZE

      try {
        const result = await listHistory(caseId, {
          ...currentFilter,
          limit: DEFAULT_PAGE_SIZE,
          offset,
        })
        setEntries(result.data)
        setTotal(result.total)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch history')
        setEntries([])
        setTotal(0)
      } finally {
        setLoading(false)
      }
    },
    [caseId]
  )

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }

    debounceRef.current = setTimeout(() => {
      void fetchData(filter, page)
    }, DEBOUNCE_MS)

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [fetchData, filter, page])

  const setFilter = useCallback((partial: Partial<HistoryFilter>) => {
    setFilterState((prev) => ({ ...prev, ...partial }))
    setPage(DEFAULT_PAGE)
  }, [])

  const refetch = useCallback(async () => {
    await fetchData(filter, page)
  }, [fetchData, filter, page])

  useInvalidationSubscription('case_activity', refetch)

  return {
    entries,
    total,
    loading,
    error,
    filter,
    setFilter,
    page,
    setPage,
    pageSize: DEFAULT_PAGE_SIZE,
    refetch,
  }
}
